import { useState, useEffect } from "react";
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Center,
  Container,
  Drawer,
  Group,
  Image,
  Loader,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import {
  IconChevronRight,
  IconRefresh,
  IconThumbDown,
  IconThumbUp,
} from "@tabler/icons-react";
import { getAdminFeedbackOverview, getAdminFeedbackVotes } from "../../api";

const DIMENSION_LABELS = {
  plugin: "Plugin",
  operation: "Operation",
  model: "Model",
};

function subjectLabel(subject) {
  if (!subject) return "Unknown";
  const i = subject.indexOf(":");
  return i === -1 ? subject : subject.slice(i + 1);
}

function deltaTone(delta) {
  if (delta <= -5) return "red";
  if (delta < 0) return "orange";
  return "green";
}

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

export default function FeedbackOverview() {
  const [groupBy, setGroupBy] = useState("plugin");
  const [days, setDays] = useState(7);
  const [minVotes, setMinVotes] = useState(1);
  const [rows, setRows] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    getAdminFeedbackOverview(groupBy, days, minVotes)
      .then((data) => setRows(data.rows ?? []))
      .finally(() => setIsLoading(false));
  }, [groupBy, days, minVotes, forceRefresh]);

  if (!rows)
    return (
      <Center mt="xl">
        <Loader size="xl" />
      </Center>
    );

  const broken = rows.filter((r) => r.total >= 3 && r.positivePct < 50);
  const deteriorating = rows.filter((r) => r.deltaPct <= -5);

  return (
    <Container px={0} fluid>
      <Group mb="sm">
        <Select
          value={groupBy}
          allowDeselect={false}
          onChange={(v) => {
            if (!v) return;
            setGroupBy(v);
            setSelected(null);
          }}
          data={[
            { value: "plugin", label: "Group by plugin" },
            { value: "operation", label: "Group by operation" },
            { value: "model", label: "Group by model" },
          ]}
        />
        <Select
          value={String(days)}
          allowDeselect={false}
          onChange={(v) => v && setDays(Number(v))}
          data={[
            { value: "7", label: "Last 7 days" },
            { value: "30", label: "Last 30 days" },
          ]}
        />
        <Select
          value={String(minVotes)}
          allowDeselect={false}
          onChange={(v) => v && setMinVotes(Number(v))}
          data={[
            { value: "1", label: "All buckets" },
            { value: "3", label: "3+ votes" },
          ]}
        />
        {isLoading ? (
          <Loader size="sm" />
        ) : (
          <ActionIcon onClick={() => setForceRefresh(forceRefresh + 1)}>
            <IconRefresh size={16} />
          </ActionIcon>
        )}
        {broken.length > 0 && (
          <Badge color="red" variant="light">
            {broken.length} struggling
          </Badge>
        )}
        {deteriorating.length > 0 && (
          <Badge color="orange" variant="light">
            {deteriorating.length} declining
          </Badge>
        )}
      </Group>

      {rows.length === 0 ? (
        <Text c="dimmed" fz="sm">
          No feedback yet. Votes appear here once users rate their results.
        </Text>
      ) : (
        <Table stickyHeader highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{DIMENSION_LABELS[groupBy]}</Table.Th>
              <Table.Th ta="right">Votes</Table.Th>
              <Table.Th ta="right">Up</Table.Th>
              <Table.Th ta="right">Down</Table.Th>
              <Table.Th ta="right">Satisfaction</Table.Th>
              <Table.Th ta="right">Prev. window</Table.Th>
              <Table.Th ta="right">Trend</Table.Th>
              <Table.Th w={28} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r) => {
              const label = subjectLabel(r.subject);
              const struggling = r.total >= 3 && r.positivePct < 50;
              const active = selected?.subject === r.subject;
              return (
                <Table.Tr
                  key={r.subject}
                  style={{ cursor: "pointer" }}
                  bg={active ? "var(--mantine-color-default-hover)" : undefined}
                  onClick={() =>
                    setSelected({
                      subject: r.subject,
                      label,
                      up: r.up,
                      down: r.down,
                      total: r.total,
                    })
                  }
                >
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Text
                        fz="sm"
                        c={struggling ? "red" : undefined}
                        fw={struggling ? 600 : 400}
                      >
                        {label}
                      </Text>
                      {struggling && (
                        <Badge size="xs" color="red" variant="light">
                          broken
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td ta="right">{r.total}</Table.Td>
                  <Table.Td ta="right" c="green">
                    {r.up}
                  </Table.Td>
                  <Table.Td ta="right" c="red">
                    {r.down}
                  </Table.Td>
                  <Table.Td ta="right">{r.positivePct}%</Table.Td>
                  <Table.Td ta="right">
                    {r.previousTotal > 0 ? `${r.previousPositivePct}%` : "–"}
                  </Table.Td>
                  <Table.Td ta="right">
                    {r.previousTotal > 0 ? (
                      <Text
                        fz="sm"
                        c={deltaTone(r.deltaPct)}
                        fw={600}
                        display="inline"
                      >
                        {r.deltaPct > 0 ? "+" : ""}
                        {r.deltaPct}pt
                      </Text>
                    ) : (
                      "–"
                    )}
                  </Table.Td>
                  <Table.Td>
                    <IconChevronRight size={16} />
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      )}

      <VoteDrawer
        selected={selected}
        groupBy={groupBy}
        days={days}
        onClose={() => setSelected(null)}
      />
    </Container>
  );
}

function VoteDrawer({ selected, groupBy, days, onClose }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const pageSize = 40;

  useEffect(() => {
    setPage(1);
    setData(null);
  }, [selected?.subject, groupBy, days]);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    getAdminFeedbackVotes(groupBy, selected.subject, days, page, pageSize)
      .then(setData)
      .finally(() => setLoading(false));
  }, [selected, groupBy, days, page]);

  return (
    <Drawer
      opened={!!selected}
      onClose={onClose}
      position="right"
      size="lg"
      title={
        selected ? (
          <Stack gap={4}>
            <Text fw={600}>{selected.label}</Text>
            <Group gap="xs">
              <Text fz="sm" c="dimmed">
                {DIMENSION_LABELS[groupBy]}
              </Text>
              <Badge color="green" variant="light" size="sm">
                {selected.up} up
              </Badge>
              <Badge color="red" variant="light" size="sm">
                {selected.down} down
              </Badge>
              <Text fz="sm" c="dimmed">
                {selected.total} votes
              </Text>
            </Group>
          </Stack>
        ) : null
      }
    >
      {loading && !data ? (
        <Center mt="xl">
          <Loader />
        </Center>
      ) : data?.votes?.length ? (
        <Stack gap="sm">
          {data.votes.map((v) => (
            <VoteRow key={v.id} vote={v} groupBy={groupBy} />
          ))}
          {data.totalCount > pageSize && (
            <Pagination
              total={Math.ceil(data.totalCount / pageSize)}
              value={page}
              onChange={setPage}
              size="sm"
            />
          )}
        </Stack>
      ) : (
        <Text c="dimmed" fz="sm">
          No votes in this window.
        </Text>
      )}
    </Drawer>
  );
}

function VoteRow({ vote, groupBy }) {
  const up = vote.score > 0;
  const otherBadges = [
    groupBy !== "plugin" ? vote.pluginSubject : null,
    groupBy !== "operation" ? vote.actionSubject : null,
    groupBy !== "model" ? vote.modelSubject : null,
  ]
    .filter(Boolean)
    .map(subjectLabel);

  return (
    <Group
      wrap="nowrap"
      align="flex-start"
      gap="sm"
      p="xs"
      style={{
        border: "1px solid var(--mantine-color-default-border)",
        borderRadius: "var(--mantine-radius-md)",
      }}
    >
      <Box
        w={72}
        h={72}
        style={{
          flexShrink: 0,
          borderRadius: 6,
          overflow: "hidden",
          background: "var(--mantine-color-dark-6)",
        }}
      >
        {vote.thumbUrl ? (
          <Anchor href={vote.thumbUrl} target="_blank" display="block">
            <Image src={vote.thumbUrl} w={72} h={72} fit="cover" />
          </Anchor>
        ) : (
          <Center h={72}>
            <Text fz="xs" c="dimmed">
              gone
            </Text>
          </Center>
        )}
      </Box>
      <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
        <Group gap={6} wrap="nowrap">
          {up ? (
            <IconThumbUp size={16} color="var(--mantine-color-green-6)" />
          ) : (
            <IconThumbDown size={16} color="var(--mantine-color-red-6)" />
          )}
          <Text fz="sm" fw={600}>
            {up ? "Up" : "Down"}
          </Text>
          <Text fz="xs" c="dimmed">
            {formatWhen(vote.createdAt)}
          </Text>
        </Group>
        {vote.userLabel && (
          <Anchor
            href={`/admin/users/${vote.userId}`}
            fz="sm"
            c="dimmed"
            underline="hover"
          >
            {vote.userLabel}
          </Anchor>
        )}
        {otherBadges.length > 0 && (
          <Group gap={4}>
            {otherBadges.map((b) => (
              <Badge key={b} size="xs" variant="light">
                {b}
              </Badge>
            ))}
          </Group>
        )}
      </Stack>
    </Group>
  );
}
